'use client';

import { memo } from 'react';
import { TableCell, TableRow, Chip, IconButton, Tooltip, Box, Typography } from '@mui/material';
import { Visibility as ViewIcon, Download as DownloadIcon } from '@mui/icons-material';
import { getStatusStyle } from '../../../constants/statusStyles';
import { formatTableDate, formatTableCurrency, getAddressLabel } from '../../../utils/tableUtils';

/**
 * Renders a single table row with cells based on column configuration
 */
const SortableTableRow = memo(({ row, columns, onRowClick, onDownload, index }) => {
  const renderCellContent = (col, value, row) => {
    switch (col.type) {
      case 'status': {
        const style = getStatusStyle(value);
        return (
          <Chip
            label={value?.replace(/_/g, ' ') || 'Unknown'}
            size="small"
            sx={{
              bgcolor: style.bg,
              color: style.color,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
            }}
          />
        );
      }
      case 'date':
        return formatTableDate(value);
      case 'currency':
        return formatTableCurrency(value);
      case 'id':
        return (
          <Typography sx={{ fontWeight: 600, color: '#6366f1', fontSize: '0.85rem' }}>
            #{value}
          </Typography>
        );
      case 'address':
        return (
          <Tooltip title={getAddressLabel(value)} arrow>
            <Typography
              sx={{
                maxWidth: col.maxWidth || 180,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.85rem',
              }}
            >
              {getAddressLabel(value)}
            </Typography>
          </Tooltip>
        );
      default:
        if (col.render) {
          return col.render(value, row);
        }
        return value ?? '-';
    }
  };

  const status = row.parcelStatus || row.status;
  const canDownload = onDownload && status?.toUpperCase() === 'DELIVERED';

  return (
    <TableRow
      hover
      onClick={() => onRowClick?.(row)}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: '#f9fafb' },
        '&:last-child td': { borderBottom: 0 },
      }}
    >
      {columns.map((col) => (
        <TableCell
          key={col.field}
          align={col.align || 'left'}
          sx={{ py: 1.5, fontSize: '0.85rem', color: '#374151' }}
        >
          {renderCellContent(col, row[col.field], row)}
        </TableCell>
      ))}
      <TableCell align="center" sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRowClick?.(row);
              }}
              sx={{ color: '#6366f1' }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canDownload && (
            <Tooltip title="Download Invoice">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(row);
                }}
                sx={{ color: '#10b981' }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
});

SortableTableRow.displayName = 'SortableTableRow';

export default SortableTableRow;
